import './css/common.css'
import './css/app.css'
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { AlertTriangle, Check, Edit, Plus, Search, X } from 'lucide-react';
import { change_Current_Location, changeHeaderTitle } from '../store';
import List from './component/list/list';
import { LoadingModal, MessageModal } from './component/modal/modal';
import OnuManageTabs from './component/onu/OnuManageTabs';
import PageHelp from './component/help/PageHelp';
import ManualDownloadLink from './component/manual/ManualDownloadLink';

const API_BASE = "/api/noti-bypass-targets";
const MAX_VISIBLE_TABLE_ROWS = 20;
const TABLE_ROW_HEIGHT = 32;
const TABLE_HEADER_HEIGHT = 44;

const FALLBACK_DEFAULTS = {
    vendor: "onu",
    endpoint_url: "https://mms.onupayment.co.kr/notification/fastpay",
    method: "GET",
    use_yn: "Y",
    memo: "O&U bypass",
};

const INITIAL_FILTERS = {
    use_yn: "",
    mcht_id: "",
    limit: 50,
};

const EMPTY_FORM = {
    vendor: "onu",
    mcht_id: "",
    endpoint_url: "https://mms.onupayment.co.kr/notification/fastpay",
    method: "GET",
    use_yn: "Y",
    memo: "O&U bypass",
};

function isSuccess(responseData) {
    return responseData?.code === 1000;
}

function getErrorMessage(responseData, fallback) {
    return responseData?.msg || fallback;
}


function formatKstDateTime(value) {
    if (value === null || value === undefined || value === "") return "-";
    const raw = String(value).trim();
    if (!raw) return "-";

    const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
    const hasTimezone = /(Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
    const parsed = new Date(hasTimezone ? normalized : normalized + "Z");
    if (Number.isNaN(parsed.getTime())) return raw;

    const kst = new Date(parsed.getTime() + (9 * 60 * 60 * 1000));
    const pad = (num) => String(num).padStart(2, "0");
    return [
        kst.getUTCFullYear(),
        pad(kst.getUTCMonth() + 1),
        pad(kst.getUTCDate()),
    ].join("-") + " " + [
        pad(kst.getUTCHours()),
        pad(kst.getUTCMinutes()),
        pad(kst.getUTCSeconds()),
    ].join(":");
}

function NotiBypassTargets() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const MyData = useSelector((state) => state.MyData);
    const anthScreen = useSelector((state) => state.anthScreen);

    const canMutate =
        ["SUPER", "ADMIN"].includes(MyData?.role) &&
        anthScreen.includes("오앤유 관리");

    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [listLoading, setListLoading] = useState(false);
    const [targets, setTargets] = useState([]);
    const [meta, setMeta] = useState({
        defaults: FALLBACK_DEFAULTS,
        allowed_methods: ["GET", "POST", "PUT", "PATCH", "DELETE"],
        allowed_use_yn: ["Y", "N"],
    });
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPage, setTotalPage] = useState(1);
    const [modalMode, setModalMode] = useState("");
    const [form, setForm] = useState(EMPTY_FORM);
    const [selectedTarget, setSelectedTarget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [duplicatePayload, setDuplicatePayload] = useState(null);
    const [toggleConfirm, setToggleConfirm] = useState(null);

    const defaultValues = useMemo(() => ({
        ...FALLBACK_DEFAULTS,
        ...(meta?.defaults ?? {}),
    }), [meta]);

    const fetchDefaults = useCallback(async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_API + API_BASE + "/defaults", {
                withCredentials: true,
            });
            const tmp = response?.data;
            if (isSuccess(tmp)) {
                const data = tmp?.data ?? {};
                const defaults = data?.defaults ?? data;
                setMeta((prev) => ({
                    ...prev,
                    ...(data?.meta ?? {}),
                    defaults: {
                        ...FALLBACK_DEFAULTS,
                        ...(defaults ?? {}),
                    },
                    allowed_methods: data?.allowed_methods ?? prev.allowed_methods,
                    allowed_use_yn: data?.allowed_use_yn ?? prev.allowed_use_yn,
                }));
            }
        } catch (error) {
            console.error(error);
        }
    }, []);

    const fetchTargets = useCallback(async () => {
        setListLoading(true);
        try {
            const params = {
                vendor: "onu",
                page,
                limit: appliedFilters.limit,
            };
            if (appliedFilters.mcht_id) params.mcht_id = appliedFilters.mcht_id;
            if (appliedFilters.use_yn) params.use_yn = appliedFilters.use_yn;

            const response = await axios.get(process.env.REACT_APP_API + API_BASE, {
                withCredentials: true,
                params,
            });
            const tmp = response?.data;
            if (isSuccess(tmp)) {
                const data = tmp?.data ?? {};
                setTargets(data.targets ?? []);
                setTotal(data.total ?? 0);
                setTotalPage(data.totalPage ?? 1);
                setPage(data.currentPage ?? page);
                setMeta((prev) => ({
                    ...prev,
                    ...(data.meta ?? {}),
                    defaults: {
                        ...FALLBACK_DEFAULTS,
                        ...(data.meta?.defaults ?? prev.defaults ?? {}),
                    },
                    allowed_methods: data.meta?.allowed_methods ?? prev.allowed_methods,
                    allowed_use_yn: data.meta?.allowed_use_yn ?? prev.allowed_use_yn,
                }));
            } else if (tmp?.code === 9999) {
                setMessage({ msg: getErrorMessage(tmp, "로그인이 필요합니다.") });
                navigate("/");
            } else {
                setMessage({ msg: getErrorMessage(tmp, "노티 바이패스 대상 목록 조회에 실패했습니다.") });
            }
        } catch (error) {
            console.error(error);
            setMessage({ msg: "노티 바이패스 대상 목록 조회 중 오류가 발생했습니다." });
        } finally {
            setLoading(false);
            setListLoading(false);
        }
    }, [appliedFilters, navigate, page]);


    useEffect(() => {
        dispatch(change_Current_Location("오앤유 관리"));
        dispatch(changeHeaderTitle({
            mainTitle: "관리자 메뉴",
            subTitle: "오앤유 관리",
        }));
        fetchDefaults();
    }, [dispatch, fetchDefaults]);

    useEffect(() => {
        fetchTargets();
    }, [fetchTargets]);

    const openCreateModal = () => {
        setSelectedTarget(null);
        setForm({
            ...EMPTY_FORM,
            ...defaultValues,
            mcht_id: "",
        });
        setModalMode("create");
    };

    const openEditModal = (target) => {
        setSelectedTarget(target);
        setForm({
            vendor: target.vendor ?? defaultValues.vendor,
            mcht_id: target.mcht_id ?? "",
            endpoint_url: target.endpoint_url ?? defaultValues.endpoint_url,
            method: target.method ?? defaultValues.method,
            use_yn: target.use_yn ?? defaultValues.use_yn,
            memo: target.memo ?? "",
        });
        setModalMode("edit");
    };

    const closeFormModal = () => {
        if (saving) return;
        setModalMode("");
        setSelectedTarget(null);
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const submitSearch = () => {
        setPage(1);
        setAppliedFilters(filters);
    };

    const resetSearch = () => {
        setFilters(INITIAL_FILTERS);
        setAppliedFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const handleFormChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const saveTarget = async (duplicateAction = "error") => {
        if (!form.mcht_id?.trim()) {
            setMessage({ msg: "가맹점 ID를 입력해주세요." });
            return;
        }
        if (!canMutate) {
            setMessage({ msg: "등록/수정 권한이 없습니다." });
            return;
        }

        const payload = {
            vendor: form.vendor?.trim() || "onu",
            mcht_id: form.mcht_id?.trim(),
            endpoint_url: form.endpoint_url?.trim() || defaultValues.endpoint_url,
            method: form.method,
            use_yn: form.use_yn,
            memo: form.memo ?? "",
        };

        setSaving(true);
        try {
            const url = modalMode === "edit"
                ? `${process.env.REACT_APP_API}${API_BASE}/${selectedTarget?.id}/update`
                : `${process.env.REACT_APP_API}${API_BASE}`;
            const body = modalMode === "edit"
                ? payload
                : { ...payload, duplicate_action: duplicateAction };

            const response = await axios.post(url, body, { withCredentials: true });
            const tmp = response?.data;
            if (isSuccess(tmp)) {
                setMessage({ msg: tmp?.msg || (modalMode === "edit" ? "수정되었습니다." : "등록되었습니다.") });
                setModalMode("");
                setSelectedTarget(null);
                setDuplicatePayload(null);
                fetchTargets();
            } else if (tmp?.code === 4090 && modalMode === "create" && duplicateAction === "error") {
                setDuplicatePayload(payload);
            } else if (tmp?.code === 9999) {
                setMessage({ msg: getErrorMessage(tmp, "로그인 또는 권한을 확인해주세요.") });
                navigate("/");
            } else {
                setMessage({ msg: getErrorMessage(tmp, modalMode === "edit" ? "수정에 실패했습니다." : "등록에 실패했습니다.") });
            }
        } catch (error) {
            console.error(error);
            setMessage({ msg: modalMode === "edit" ? "수정 중 오류가 발생했습니다." : "등록 중 오류가 발생했습니다." });
        } finally {
            setSaving(false);
        }
    };

    const retryDuplicate = async (action) => {
        if (!duplicatePayload) return;
        setForm((prev) => ({ ...prev, ...duplicatePayload }));
        setDuplicatePayload(null);
        await saveTarget(action);
    };

    const requestToggleUseYn = (target, nextUseYn) => {
        if (!canMutate) {
            setMessage({ msg: "활성/비활성 변경 권한이 없습니다." });
            return;
        }
        if (target.use_yn === nextUseYn) return;
        if (nextUseYn === "Y") {
            toggleUseYn(target, nextUseYn);
            return;
        }
        setToggleConfirm({ target, nextUseYn });
    };

    const toggleUseYn = async (target, nextUseYn) => {
        if (!canMutate) {
            setMessage({ msg: "활성/비활성 변경 권한이 없습니다." });
            return;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API}${API_BASE}/${target.id}/use-yn`,
                { use_yn: nextUseYn },
                { withCredentials: true }
            );
            const tmp = response?.data;
            if (isSuccess(tmp)) {
                const updatedTarget = tmp?.data?.target;
                if (updatedTarget) {
                    setTargets((prev) => prev.map((item) => item.id === target.id ? updatedTarget : item));
                } else {
                    fetchTargets();
                }
                setMessage({ msg: tmp?.msg || "상태가 변경되었습니다." });
            } else if (tmp?.code === 9999) {
                setMessage({ msg: getErrorMessage(tmp, "로그인 또는 권한을 확인해주세요.") });
                navigate("/");
            } else {
                setMessage({ msg: getErrorMessage(tmp, "상태 변경에 실패했습니다.") });
            }
        } catch (error) {
            console.error(error);
            setMessage({ msg: "상태 변경 중 오류가 발생했습니다." });
        }
    };

    const tableHeaderData = [
        {
            no: 1,
            text: (
                <HeaderControl>
                    <input
                        value={filters.mcht_id}
                        onChange={(event) => handleFilterChange("mcht_id", event.target.value)}
                        placeholder="가맹점 ID"
                        style={headerInputStyle}
                        onKeyDown={(event) => event.key === "Enter" && submitSearch()}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    />
                </HeaderControl>
            ),
            width: "220px",
            textAlign: "center"
        },
        {
            no: 2,
            text: (
                <HeaderControl>
                    <select
                        value={filters.use_yn}
                        onChange={(event) => handleFilterChange("use_yn", event.target.value)}
                        style={headerInputStyle}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <option value="">활성 여부</option>
                        <option value="Y">활성</option>
                        <option value="N">비활성</option>
                    </select>
                </HeaderControl>
            ),
            width: "140px",
            textAlign: "center"
        },
        { no: 3, text: <HeaderCell label="수정일시" />, width: "170px", textAlign: "center" },
        {
            no: 4,
            text: (
                <HeaderControl>
                    <div
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        style={{ display: "flex", justifyContent: "center", gap: "4px", width: "100%" }}
                    >
                        <select
                            value={filters.limit}
                            onChange={(event) => handleFilterChange("limit", Number(event.target.value))}
                            style={{ ...headerInputStyle, width: "76px" }}
                        >
                            <option value={50}>50건</option>
                            <option value={100}>100건</option>
                            <option value={200}>200건</option>
                        </select>
                        <button type="button" title="검색" onClick={submitSearch} style={headerIconButtonStyle}>
                            <Search size={14} />
                        </button>
                        <button type="button" title="초기화" onClick={resetSearch} style={headerIconButtonStyle}>
                            <X size={14} />
                        </button>
                    </div>
                </HeaderControl>
            ),
            width: "190px",
            textAlign: "center"
        },
        { no: 5, text: <HeaderCell label="전송 URL" />, width: "320px", flex: 1, textAlign: "center" },
    ];

    const tableBodyData = targets.map((target) => {
        const isActive = target.use_yn === "Y";
        return [
            { no: 1, text: target.mcht_id ?? "-", width: "220px", textAlign: "left" },
            {
                no: 2,
                text: (
                    <label
                        className="switch"
                        title={isActive ? "활성" : "비활성"}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <input
                            type="checkbox"
                            checked={isActive}
                            disabled={!canMutate}
                            onChange={(event) => requestToggleUseYn(target, event.target.checked ? "Y" : "N")}
                        />
                        <span className="slider"></span>
                    </label>
                ),
                width: "140px",
                textAlign: "center",
            },
            { no: 3, text: formatKstDateTime(target.updated_at), width: "170px", textAlign: "center" },
            {
                no: 4,
                text: (
                    <button
                        className="btn btn-primary"
                        style={{ padding: "4px 8px", fontSize: "12px" }}
                        onClick={(event) => {
                            event.stopPropagation();
                            openEditModal(target);
                        }}
                        disabled={!canMutate}
                        title="수정"
                    >
                        <Edit size={14} />
                    </button>
                ),
                width: "190px",
                textAlign: "center",
            },
            {
                no: 5,
                text: target.endpoint_url || "-",
                width: "320px",
                flex: 1,
                textAlign: "left",
                style: { wordBreak: "break-all" },
            },
        ];
    });

    const tableHeight = tableBodyData.length > 0
        ? `min(calc(100vh - 320px), ${Math.max(Math.min(tableBodyData.length, MAX_VISIBLE_TABLE_ROWS) * TABLE_ROW_HEIGHT + TABLE_HEADER_HEIGHT, 96)}px)`
        : "96px";

    if (loading) {
        return <LoadingModal />;
    }

    return (
        <div style={{ padding: "20px", marginTop: "52px" }}>
            <MessageModal message={message} />

            <div style={pageHeaderStyle}>
                <div>
                    <div style={titleRowStyle}>
                        <h2 style={{ fontSize: "22px", fontWeight: 700, marginBottom: 0 }}>노티 바이패스 대상 관리</h2>
                        <ManualDownloadLink
                            href="/manuals/onu-bypass-manual-a4.svg"
                            fileName="onu-bypass-manual-a4.svg"
                            tone="teal"
                        />
                        <PageHelp
                            tone="blue"
                            width="520px"
                            sections={[
                                {
                                    title: "설명",
                                    items: [
                                        "오앤유로 노티를 함께 전달할 가맹점을 관리하는 화면입니다.",
                                        "현재 운영 화면에서는 오앤유 대상만 관리합니다.",
                                    ],
                                },
                                {
                                    title: "가이드",
                                    items: [
                                        "가맹점 ID로 먼저 검색해 이미 등록되어 있는지 확인합니다.",
                                        "없으면 신규 등록을 눌러 가맹점 ID만 입력합니다.",
                                        "등록 후 활성 상태이면 오앤유 노티 전달 대상에 포함됩니다.",
                                        "잠시 제외해야 하는 가맹점은 활성 토글을 끕니다.",
                                        "다시 전달 대상에 포함하려면 활성 토글을 켭니다.",
                                    ],
                                },
                                {
                                    title: "주의사항",
                                    items: [
                                        "비활성 상태인 가맹점은 오앤유 노티 전달 대상에서 제외됩니다.",
                                        "가맹점 ID를 잘못 입력하면 다른 가맹점에 대한 설정이 될 수 있으니 저장 전 한 번 더 확인합니다.",
                                        "가맹점 ID 등록 전 발생한 거래에 대해서는 개발팀에 문의 부탁드립니다.",
                                    ],
                                },
                            ]}
                        />
                    </div>
                    <div style={{ color: "#64748b", fontSize: "13px", marginTop: "4px" }}>오앤유 관리</div>
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button className="btn btn-primary" onClick={openCreateModal} disabled={!canMutate}>
                        <Plus size={16} style={{ marginRight: "5px" }} />
                        신규 등록
                    </button>
                </div>
            </div>

            <OnuManageTabs />

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", color: "#475569", fontSize: "13px" }}>
                <span>총 {total.toLocaleString()}건</span>
                <span>{page.toLocaleString()} / {totalPage.toLocaleString()} 페이지</span>
            </div>

            <List
                tableHeaderData={tableHeaderData}
                tableBodyData={tableBodyData}
                filter_tableBodyData={tableBodyData}
                page={1}
                setPage={() => { }}
                maxFirstPage={tableBodyData.length || 1}
                height={tableHeight}
                nonePage={false}
                spinner={listLoading}
                syncHorizontalScroll={true}
            />

            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginTop: "12px" }}>
                <button
                    className="btn"
                    disabled={page <= 1 || listLoading}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                >
                    이전
                </button>
                <span style={{ minWidth: "90px", textAlign: "center", color: "#334155" }}>{page} / {totalPage}</span>
                <button
                    className="btn"
                    disabled={page >= totalPage || listLoading}
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPage))}
                >
                    다음
                </button>
            </div>

            {modalMode && (
                <TargetFormModal
                    mode={modalMode}
                    form={form}
                    saving={saving}
                    onChange={handleFormChange}
                    onClose={closeFormModal}
                    onSave={() => saveTarget("error")}
                />
            )}

            {toggleConfirm && (
                <ToggleUseYnConfirmModal
                    target={toggleConfirm.target}
                    nextUseYn={toggleConfirm.nextUseYn}
                    onClose={() => setToggleConfirm(null)}
                    onConfirm={async () => {
                        const pending = toggleConfirm;
                        setToggleConfirm(null);
                        await toggleUseYn(pending.target, pending.nextUseYn);
                    }}
                />
            )}

            {duplicatePayload && (
                <DuplicateModal
                    onClose={() => setDuplicatePayload(null)}
                    onActivate={() => retryDuplicate("activate")}
                    onUpdate={() => retryDuplicate("update")}
                />
            )}
        </div>
    );
}

function HeaderControl({ children }) {
    return (
        <div style={{ width: "100%", minHeight: "36px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {children}
        </div>
    );
}

function HeaderCell({ label }) {
    return (
        <HeaderControl>
            <span style={{ fontSize: "12px", fontWeight: 700, color: "#334155", lineHeight: 1.1 }}>{label}</span>
        </HeaderControl>
    );
}

function TargetFormModal({ mode, form, saving, onChange, onClose, onSave }) {
    return (
        <div className="modal" tabIndex="-1" style={{ display: "block", zIndex: 100000 }}>
            <div className="modal-dialog" style={{ width: "460px", maxWidth: "95%", padding: "18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700 }}>{mode === "edit" ? "오앤유 대상 수정" : "오앤유 대상 신규 등록"}</h3>
                    <button className="btn" onClick={onClose} disabled={saving} style={{ padding: "4px 8px" }}>
                        <X size={16} />
                    </button>
                </div>

                <div style={fixedTargetNoticeStyle}>
                    오앤유 노티 전달 대상은 가맹점 ID와 활성 여부만 관리합니다.
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px" }}>
                    <FormField label="가맹점 ID">
                        <input
                            value={form.mcht_id}
                            onChange={(event) => onChange("mcht_id", event.target.value)}
                            style={inputStyle}
                            placeholder="예: fastpay13"
                        />
                    </FormField>
                    <FormField label="활성 여부">
                        <select value={form.use_yn} onChange={(event) => onChange("use_yn", event.target.value)} style={inputStyle}>
                            <option value="Y">활성</option>
                            <option value="N">비활성</option>
                        </select>
                    </FormField>
                </div>

                <div className="modal-footer" style={{ marginTop: "18px" }}>
                    <button className="btn" onClick={onClose} disabled={saving}>취소</button>
                    <button className="btn btn-primary" onClick={onSave} disabled={saving}>
                        <Check size={16} style={{ marginRight: "5px" }} />
                        저장
                    </button>
                </div>
            </div>
        </div>
    );
}

function FormField({ label, children }) {
    return (
        <label style={{ display: "flex", flexDirection: "column", gap: "5px", color: "#334155", fontSize: "13px", fontWeight: 600 }}>
            <span>{label}</span>
            {children}
        </label>
    );
}

function ToggleUseYnConfirmModal({ target, nextUseYn, onClose, onConfirm }) {
    const isDeactivating = nextUseYn === "N";
    return (
        <div className="modal" tabIndex="-1" style={{ display: "block", zIndex: 100001 }}>
            <div className="modal-dialog" style={{ width: "480px", maxWidth: "95%", padding: "18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                    <div style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isDeactivating ? "#fef2f2" : "#ecfdf5",
                        color: isDeactivating ? "#b91c1c" : "#047857",
                    }}>
                        {isDeactivating ? <AlertTriangle size={18} /> : <Check size={18} />}
                    </div>
                    <h3 style={{ fontSize: "18px", fontWeight: 700, margin: 0 }}>
                        {isDeactivating ? "바이패스 대상을 비활성화할까요?" : "바이패스 대상을 활성화할까요?"}
                    </h3>
                </div>
                <div style={{ color: "#334155", lineHeight: 1.6, fontSize: "13px", marginBottom: "14px" }}>
                    <div><strong>가맹점 ID:</strong> {target?.mcht_id || "-"}</div>
                </div>
                {isDeactivating ? (
                    <div style={dangerNoticeStyle}>
                        비활성화하면 이 가맹점은 오앤유 노티 전달 대상에서 제외됩니다. 제외해도 되는 가맹점인지 확인한 뒤 진행하세요.
                    </div>
                ) : (
                    <div style={activateNoticeStyle}>
                        활성화하면 잠시 뒤 이 가맹점이 오앤유 노티 전달 대상에 포함됩니다.
                    </div>
                )}
                <div className="modal-footer" style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "18px" }}>
                    <button className="btn" onClick={onClose}>취소</button>
                    <button
                        className="btn"
                        onClick={onConfirm}
                        style={{
                            backgroundColor: isDeactivating ? "#b91c1c" : "#0f766e",
                            color: "#fff",
                            borderColor: isDeactivating ? "#b91c1c" : "#0f766e",
                        }}
                    >
                        {isDeactivating ? "비활성화" : "활성화"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DuplicateModal({ onClose, onActivate, onUpdate }) {
    return (
        <div className="modal" tabIndex="-1" style={{ display: "block", zIndex: 100001 }}>
            <div className="modal-dialog" style={{ width: "460px", maxWidth: "95%", padding: "18px" }}>
                <h3 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "10px" }}>이미 등록된 대상입니다.</h3>
                <p style={{ color: "#475569", lineHeight: 1.6, marginBottom: "18px" }}>
                    같은 가맹점 ID가 이미 등록되어 있습니다. 기존 항목을 다시 활성화하거나 현재 입력한 상태로 정리할 수 있습니다.
                </p>
                <div className="modal-footer" style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                    <button className="btn" onClick={onClose}>취소</button>
                    <button className="btn" onClick={onActivate} style={{ backgroundColor: "#0f766e", color: "#fff", borderColor: "#0f766e" }}>
                        기존 항목 활성화
                    </button>
                    <button className="btn btn-primary" onClick={onUpdate}>현재 상태로 정리</button>
                </div>
            </div>
        </div>
    );
}

const dangerNoticeStyle = {
    padding: "10px 12px",
    border: "1px solid #fecaca",
    borderRadius: "6px",
    backgroundColor: "#fef2f2",
    color: "#991b1b",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: 1.55,
};

const activateNoticeStyle = {
    padding: "10px 12px",
    border: "1px solid #bbf7d0",
    borderRadius: "6px",
    backgroundColor: "#f0fdf4",
    color: "#166534",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: 1.55,
};

const fixedTargetNoticeStyle = {
    marginBottom: "14px",
    padding: "10px 12px",
    border: "1px solid #bfdbfe",
    borderRadius: "6px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "13px",
    fontWeight: 700,
    lineHeight: 1.5,
};

const inputStyle = {
    width: "100%",
    minHeight: "36px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "6px 8px",
    backgroundColor: "#fff",
    fontSize: "13px",
};

const pageHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    marginBottom: "14px",
};

const titleRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
};

const headerInputStyle = {
    width: "100%",
    height: "32px",
    border: "1px solid #94a3b8",
    borderRadius: "5px",
    padding: "4px 8px",
    backgroundColor: "#fff",
    color: "#0f172a",
    fontSize: "12px",
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.08)",
};

const headerIconButtonStyle = {
    width: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #94a3b8",
    borderRadius: "5px",
    backgroundColor: "#fff",
    color: "#334155",
    cursor: "pointer",
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.08)",
};

export default NotiBypassTargets;
